import {
    Alert,
    Box,
    CircularProgress,
    Slider,
    Stack,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'

import React, { useState, FC } from 'react'
import { getDataPoint, sendAction } from '@/services/sensors.service'
import { insertAction } from '@/utils/realtime.utils'
import { useRealtime } from '../useRealtime'
import { useTranslation } from 'react-i18next'
import uniq from 'lodash/uniq'
import { useQuery } from '@tanstack/react-query'
import { Group, Sensor, DataType } from '../types'

const marks = [
    {
        value: 20,
        label: '20°C',
    },
    {
        value: 25,
        label: '25°C',
    },
]

function formatValue(value: number) {
    return `${value}°C`
}

interface GetDataPointResponse {
    measurement: {
        actuator?: {
            ActuatorId: number
            DataTypeId: number
            Min?: number
            Max?: number
        }
        sensorId: number
        data: {
            DateTime: string
            Value: number
            SensorId: number
        }[]
    }[]
}
interface ParsedGetDataPointResponse {
    setTempMin: number
    setTempMax: number
    driveActuatorId: number
    setTempActuatorId: number
}

const DEFAULT_MIN_SETTEMP = 16
const DEFAULT_MAX_SETTEMP = 30

function parseResponse(measurement: GetDataPointResponse['measurement']): ParsedGetDataPointResponse {
    const driveActuator = measurement.find((m) => m.actuator?.DataTypeId === DataType.STATE)?.actuator
    if (!driveActuator) {
        throw new Error('Drive actuator not found in GetDataPointResponse')
    }
    const setTempActuator = measurement.find((m) => m.actuator?.DataTypeId === DataType.SETPOINT_TEMPERATURE)?.actuator
    if (!setTempActuator) {
        throw new Error('Set temperature actuator not found in GetDataPointResponse')
    }
    return {
        setTempMin: setTempActuator.Min || DEFAULT_MIN_SETTEMP,
        setTempMax: setTempActuator.Max || DEFAULT_MAX_SETTEMP,
        driveActuatorId: driveActuator.ActuatorId,
        setTempActuatorId: setTempActuator.ActuatorId,
    }
}

const useGroupTelecommand = (item: Group, sensors: Sensor[]) => {
    const { site } = useRealtime()
    const { data, isLoading } = useQuery<GetDataPointResponse>({
        queryFn: async () =>
            getDataPoint({
                hash: site.hash,
                sensors: uniq(sensors),
                objectId: item?.objects?.[0],
            }),
        queryKey: ['groupTelecommand', item.hash],
        staleTime: 60 * 1000,
    })
    const parsedData = data?.measurement ? parseResponse(data.measurement) : null
    return { isLoading, parsedData, site }
}

interface GroupTelecommandProps {
    item: Group
    sensors: Sensor[]
    open: boolean
    onClose: () => void
}

const GroupTelecommandForm: FC<GroupTelecommandProps> = ({ item, sensors, open, onClose }) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.groupTelecommand' })
    const { parsedData, isLoading } = useGroupTelecommand(item, sensors)
    const { site } = useRealtime()
    const [drive, setDrive] = React.useState<boolean>(false)
    const [setTemp, setSetTemp] = React.useState<number>(20)
    const handleChangeSetTemp = (event: Event, newValue: number | number[]) => {
        setSetTemp(Array.isArray(newValue) ? newValue[0] : newValue)
    }
    const [sending, setSending] = useState(false)
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
            </Box>
        )
    }
    if (!parsedData) {
        return null
    }
    const { setTempMin, setTempMax, driveActuatorId, setTempActuatorId } = parsedData
    const submit = async () => {
        setSending(true)
        const modifications = []
        if (driveActuatorId && drive !== null) {
            modifications.push({
                id: driveActuatorId,
                value: drive ? 1 : 0,
                title: '',
                actuatorId: driveActuatorId,
            })
        }
        if (setTempActuatorId && setTemp !== null) {
            modifications.push({
                id: setTempActuatorId,
                value: setTemp,
                title: '',
                actuatorId: setTempActuatorId,
            })
        }
        if (modifications?.length !== 0) {
            await sendAction({
                groupHashes: [item.hash],
                modifications,
                hash: site?.hash,
                fromDashboard: true,
            })
            insertAction(modifications)
        }
        setSending(false)
        onClose()
    }

    return (
        <>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography>State</Typography>
                    <ToggleButtonGroup
                        value={drive}
                        exclusive
                        onChange={(event, newValue: boolean | null) => {
                            if (newValue != null) {
                                setDrive(newValue)
                            }
                        }}
                        color="primary"
                    >
                        <ToggleButton value={true}>ON</ToggleButton>
                        <ToggleButton value={false}>OFF</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box>
                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                        {t('fields.setpointTemperature')}
                    </Typography>
                    <Box sx={{ px: 2 }}>
                        <Slider
                            aria-label="Custom marks"
                            onChange={handleChangeSetTemp}
                            getAriaValueText={formatValue}
                            step={1}
                            value={setTemp}
                            valueLabelDisplay="auto"
                            marks={
                                setTempMin && setTempMax
                                    ? [
                                          { value: setTempMin, label: `${setTempMin}°C` },
                                          ...marks,
                                          { value: setTempMax, label: `${setTempMax}°C` },
                                      ]
                                    : marks
                            }
                            min={setTempMin}
                            max={setTempMax}
                        />
                    </Box>
                </Box>
                <Alert severity="warning">{t('warning')}</Alert>
            </Stack>
            <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={sending}>
                    {t('actions.cancel')}
                </Button>
                {!sending ? (
                    <Button
                        variant="contained"
                        disabled={sending}
                        onClick={submit}
                        startIcon={sending ? <CircularProgress color="inherit" size={14} /> : undefined}
                    >
                        {t('actions.save')}
                    </Button>
                ) : (
                    <CircularProgress />
                )}
            </DialogActions>
        </>
    )
}
export const GroupTelecommand: FC<GroupTelecommandProps> = (props) => {
    const { t } = useTranslation('site', { keyPrefix: 'realtime.groupTelecommand' })
    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="md">
            <DialogTitle>{t('title', { group: props.item.name })}</DialogTitle>
            <DialogContent>
                <GroupTelecommandForm {...props} />
            </DialogContent>
        </Dialog>
    )
}
